/**
 * The Me tab: progress, the level estimate and its evidence, report cards,
 * achievements, and settings (docs/04-screen-specs.md F1–F8).
 *
 * Two things here are deliberate responses to the competitor's review profile.
 * Cancellation is one tap from the top of Settings rather than buried, because
 * "subscription/refund" is a third of its negative reviews. And the level is shown with
 * its evidence and error bars, because a number nobody can interrogate is a number
 * nobody believes.
 */

import { ApiError, clientUlid, type RouteBody, type RouteResponse } from '@saysooner/api-client';
import type { AnalyticsValue } from '@saysooner/contracts';
import {
  type CancellationRoute,
  CEFR_SUBLEVELS,
  cancellationRoute,
  cefrFromIndex,
  cefrIndex,
  commitmentProjection,
  DAILY_GOAL_CHOICES,
  DIALECTS_BY_LANGUAGE,
  formatBytes,
  levelHistorySummary,
  MAX_PROJECTED_SUBLEVELS,
  planOfflineSync,
  plotOrder,
  projectProgress,
  SESSION_KIND_LABELS,
} from '@saysooner/core';
import {
  Banner,
  Button,
  CefrBadge,
  EmptyState,
  ErrorState,
  PillarCard,
  Skeleton,
  Sparkline,
  StreakStrip,
  UsageMeter,
} from '@saysooner/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useApi, useApp, useCourseId } from '../app-context.js';
import { qk, retryIf } from '../lib/client.js';
import { exportPhase, isExportStalled } from '../lib/export-status.js';
import { googleIdToken, googleSignInAvailable } from '../lib/google-sign-in.js';
import { IndexedDbStore } from '../lib/offline-store.js';
import { createPasskey, passkeysSupported } from '../lib/passkey.js';

type Progress = RouteResponse<'getProgress'>;
type CefrDetail = RouteResponse<'getCefrDetail'>;

export interface NavigationProps {
  readonly onNavigate: (route: string) => void;
}

type Window = 'week' | 'month' | 'quarter' | 'all';

// ── F1 Progress ─────────────────────────────────────────────────────────────

export function ProgressScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();
  const [window, setWindow] = useState<Window>('week');

  const query = useQuery({
    queryKey: qk.progress(courseId, window),
    queryFn: () => api.call('getProgress', { query: { courseId, window } }),
  });

  // Spoken minutes is the north-star metric, so it travels with every progress view
  // rather than being reconstructed from session records later.
  const progress = query.data;
  useEffect(() => {
    if (progress === undefined) return;
    track('progress_view', {
      cefr: progress.cefrLevel,
      scores: Object.fromEntries(progress.skills.map((skill) => [skill.pillar, skill.score])),
      // The server's figure, not the tail of the series. `slice(-7)` summed the last
      // seven *rows*, and `daily_activity` has a row only for a day the learner was
      // active — so seven sessions spread over three months were reported as a week.
      spoken_minutes_7d: Math.round(progress.spokenMinutes7d),
    });
  }, [progress, track]);

  return (
    <main id="main" className="vl-page">
      <h1>Progress</h1>

      <nav className="vl-chip-filters" aria-label="Time range">
        {(['week', 'month', 'quarter', 'all'] as const).map((option) => (
          <button
            key={option}
            type="button"
            className="vl-chip-filter"
            aria-pressed={window === option}
            onClick={() => setWindow(option)}
          >
            {option === 'all' ? 'All time' : `This ${option}`}
          </button>
        ))}
      </nav>

      {query.isPending && <Skeleton lines={6} label="Loading your progress" />}
      {query.error !== null && (
        <ErrorState
          title="We couldn't load your progress"
          {...retryIf(query.error, () => void query.refetch())}
        />
      )}

      {query.data !== undefined &&
        (() => {
          const progress: Progress = query.data;
          return (
            <>
              <section className="vl-progress__level">
                <CefrBadge
                  level={progress.cefrLevel}
                  bandLabel={progress.cefrLevel.slice(0, 2)}
                  confidence={progress.cefrConfidence}
                  bandSublevels={progress.cefrBandSublevels}
                  onOpen={() => onNavigate('/me/level')}
                />
                {/*
                  The history sparkline F1 asks for. Drawn from indices because a level
                  is ordinal, and through `plotOrder` because `cefrHistory` arrives
                  newest first while a chart reads left to right. Two estimates is the
                  floor: a single point is a dot that says nothing about a trend, and
                  this is a section about change over time.
                */}
                {progress.cefrHistory.length > 1 && (
                  <Sparkline
                    values={plotOrder(progress.cefrHistory.map((entry) => cefrIndex(entry.level)))}
                    label={`Your level over time. ${levelHistorySummary(
                      progress.cefrHistory.map((entry) => entry.level),
                    )}`}
                  />
                )}
              </section>

              {/* Spoken minutes is the north star, and it is shown to the learner
                  rather than kept as an internal metric. The thing we optimise for and
                  the thing we show should be the same thing. */}
              <section>
                <h2>Minutes actually speaking</h2>
                <SparkBars
                  values={progress.spokenMinutes.map((day) => day.minutes)}
                  labels={progress.spokenMinutes.map((day) => day.localDate)}
                  unit="minutes"
                />
                <p className="vl-progress__total">
                  {Math.round(progress.totals.spokenSeconds / 60)} minutes in total
                </p>
              </section>

              <section>
                <h2>Accuracy</h2>
                <SparkBars
                  values={progress.accuracyTrend.map((day) => day.accuracy)}
                  labels={progress.accuracyTrend.map((day) => day.localDate)}
                  unit="out of 100"
                />
              </section>

              <section>
                <h2>Your five</h2>
                <ul className="vl-pillar-grid">
                  {progress.skills.map((skill) => (
                    <li key={skill.pillar}>
                      <PillarCard
                        pillar={skill.pillar}
                        score={skill.score}
                        delta7d={skill.delta7d}
                        evaluatedAt={skill.evaluatedAt}
                        // A score from a handful of samples is not a measurement, and
                        // the card says so rather than presenting it as settled.
                        progressLabel={
                          skill.sampleSize < 10
                            ? `from ${skill.sampleSize} ${skill.sampleSize === 1 ? 'sample' : 'samples'}`
                            : undefined
                        }
                        needsWork={skill.delta7d < 0}
                        onOpen={() => onNavigate('/me/level')}
                      />
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h2>Streak</h2>
                <StreakStrip
                  strip={progress.streak.strip}
                  currentDays={progress.streak.currentDays}
                  currentWeeks={progress.streak.currentWeeks}
                  freezesLeft={progress.streak.freezesLeft}
                />
              </section>

              {/*
                The session history list F1 asks for. The session's own facts — when,
                how long, how many turns, how it scored — with the analysis left on the
                report card, which is where the tier window applies. The server windows
                this list by the same rule, so the progress home cannot become a way
                around that paywall.
              */}
              {progress.recentSessions.length > 0 && (
                <section>
                  <h2>Recent sessions</h2>
                  <ul className="vl-session-history">
                    {progress.recentSessions.map((session) => (
                      <li key={session.sessionId} className="vl-session-history__row">
                        <span className="vl-session-history__kind">
                          {SESSION_KIND_LABELS[session.kind]}
                        </span>
                        <span className="vl-session-history__when">
                          {new Date(session.startedAt).toLocaleDateString()}
                        </span>
                        <span className="vl-session-history__facts">
                          {session.minutes} min · {session.turns}{' '}
                          {session.turns === 1 ? 'turn' : 'turns'}
                          {/* No number at all rather than a zero: a text session was
                              never scored, and a withheld one is not a bad one. */}
                          {session.accuracy !== null && ` · ${Math.round(session.accuracy)}/100`}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {progress.sessionsTruncatedByTier && (
                    <Button variant="secondary" onClick={() => onNavigate('/me/reports')}>
                      See your full history
                    </Button>
                  )}
                </section>
              )}

              <dl className="vl-progress__totals">
                <div>
                  <dt>Lessons</dt>
                  <dd>{progress.totals.lessonsComplete}</dd>
                </div>
                <div>
                  <dt>Words mastered</dt>
                  <dd>{progress.totals.wordsMastered}</dd>
                </div>
                <div>
                  <dt>Sessions</dt>
                  <dd>{progress.totals.sessions}</dd>
                </div>
              </dl>

              <nav className="vl-progress__links">
                <Button variant="secondary" onClick={() => onNavigate('/me/reports')}>
                  Report cards
                </Button>
                <Button variant="secondary" onClick={() => onNavigate('/me/achievements')}>
                  Achievements
                </Button>
                <Button variant="secondary" onClick={() => onNavigate('/me/settings')}>
                  Settings
                </Button>
              </nav>
            </>
          );
        })()}
    </main>
  );
}

/** A bar chart with every value also available as text, so it is not colour-only. */
function SparkBars({
  values,
  labels,
  unit,
}: {
  readonly values: readonly number[];
  readonly labels: readonly string[];
  readonly unit: string;
}): JSX.Element {
  const max = Math.max(1, ...values);
  return (
    <ol className="vl-spark" aria-label={`Daily ${unit}`}>
      {values.map((value, index) => (
        <li
          // Keyed by the date rather than the position, so a changed window re-keys.
          key={`${labels[index] ?? index}`}
          className="vl-spark__bar"
          style={{ height: `${Math.round((value / max) * 100)}%` }}
        >
          <span className="vl-visually-hidden">
            {labels[index]}: {Math.round(value)} {unit}
          </span>
        </li>
      ))}
    </ol>
  );
}

// ── F2 Level detail ─────────────────────────────────────────────────────────

export function LevelScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();
  const query = useQuery({
    queryKey: ['cefr', courseId] as const,
    queryFn: () => api.call('getCefrDetail', { query: { courseId } }),
  });
  // The projection needs the learner's own daily goal, so the timeline moves when they
  // change it rather than on the next fetch. Same source as the settings screen.
  const courses = useQuery({ queryKey: qk.courses, queryFn: () => api.call('listCourses') });
  const dailyGoalMinutes =
    courses.data?.courses.find((entry) => entry.id === courseId)?.dailyGoalMinutes ?? 0;

  // The confidence and the source, not just the level. A level shown without them is
  // the competitor's mistake, and recording them is how we check we are not repeating
  // it at low sample sizes.
  const detailData = query.data;
  useEffect(() => {
    if (detailData === undefined) return;
    track('cefr_detail_view', {
      level: detailData.level,
      confidence: detailData.confidence,
      band_sublevels: detailData.bandSublevels,
      // The estimate's own source lives on its history entry, not at the top level,
      // and the history is newest *first* — `at(-1)` reported the oldest estimate's
      // source in the event that exists to tell us whether the level is believed.
      source: detailData.history[0]?.source ?? null,
    });
  }, [detailData, track]);

  if (query.isPending) return <Skeleton lines={6} label="Loading your level" />;
  if (query.error !== null || query.data === undefined) {
    return (
      <ErrorState
        title="We couldn't load your level"
        {...retryIf(query.error, () => void query.refetch())}
      />
    );
  }

  const detail: CefrDetail = query.data;

  return (
    <main id="main" className="vl-page">
      <h1>Your level</h1>
      <CefrBadge
        level={detail.level}
        bandLabel={detail.bandLabel}
        confidence={detail.confidence}
        bandSublevels={detail.bandSublevels}
      />

      {/* The sample size, stated. A level from four utterances is not a measurement,
          and presenting it as one is how learners stop trusting the number. */}
      <p className="vl-page__lede">
        Based on {detail.sampleSize} scored {detail.sampleSize === 1 ? 'sample' : 'samples'}. We are{' '}
        {Math.round(detail.confidence * 100)}% confident, and it sharpens as you speak.
      </p>

      <section>
        <h2>What you can do</h2>
        <ul className="vl-can-do">
          {detail.canDo.map((statement) => (
            <li key={statement}>{statement}</li>
          ))}
        </ul>
      </section>

      <section>
        <h2>Not yet</h2>
        {/* Framed as "not yet" rather than "cannot", and specific enough to work on. */}
        <ul className="vl-cannot-yet">
          {detail.cannotYet.map((statement) => (
            <li key={statement}>{statement}</li>
          ))}
        </ul>
      </section>

      <section>
        <h2>How we worked this out</h2>
        {/* The evidence, with weights. A learner who disagrees can see exactly which
            component moved the estimate. */}
        <table className="vl-evidence">
          <thead>
            <tr>
              <th scope="col">Signal</th>
              <th scope="col">Score</th>
              <th scope="col">Weight</th>
            </tr>
          </thead>
          <tbody>
            {detail.evidence.map((row) => (
              // Which component a learner inspects is the useful signal: the whole
              // reason the evidence is shown is that the competitor's unexplained
              // level is not believed, and this is how we find out if it helps.
              <tr
                key={row.component}
                onFocus={() => track('cefr_evidence_expand', { component: row.component })}
              >
                <td>{row.component}</td>
                <td>{Math.round(row.score)}</td>
                <td>{Math.round(row.weight * 100)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <ProjectedPath detail={detail} dailyGoalMinutes={dailyGoalMinutes} onNavigate={onNavigate} />

      {detail.history.length > 1 && (
        <section>
          <h2>Over time</h2>
          <ol className="vl-level-history">
            {detail.history.map((entry) => (
              <li key={entry.at}>
                {new Date(entry.at).toLocaleDateString()} · {entry.level} (
                {Math.round(entry.confidence * 100)}% confident) · {entry.source}
              </li>
            ))}
          </ol>
        </section>
      )}

      {detail.retestAvailable && (
        <Button
          onClick={() => {
            track('cefr_retest_tap', { available: true });
            onNavigate('/learn/placement');
          }}
        >
          Re-test my level
        </Button>
      )}
    </main>
  );
}

/**
 * The forward path, or an honest statement that we will not draw one.
 *
 * Every competitor shows this before the learner has practised once, so the line says
 * the same thing to everyone (docs/research/16-category-leaders-2026-10-20.md §4). Ours
 * is divided out of minutes this learner actually spent, and when there is nothing to
 * divide it says so instead — the same choice the score card makes when it withholds.
 */
function ProjectedPath({
  detail,
  dailyGoalMinutes,
  onNavigate,
}: {
  detail: CefrDetail;
  dailyGoalMinutes: number;
  onNavigate: NavigationProps['onNavigate'];
}): JSX.Element {
  // The horizon, not a goal the learner set: the product has no "target level" concept,
  // and inventing one here would put a number in their plan that they never chose.
  const target = cefrFromIndex(
    Math.min(cefrIndex(detail.level) + MAX_PROJECTED_SUBLEVELS, CEFR_SUBLEVELS.length - 1),
  );
  const projection = projectProgress({
    current: detail.level,
    confidence: detail.confidence,
    target,
    dailyGoalMinutes,
    pace: detail.paceWindow,
    now: Date.now(),
  });

  if (projection.status === 'withheld') {
    return (
      <section>
        <h2>What comes next</h2>
        <p className="vl-page__lede">
          {WITHHELD_COPY[projection.withheldReason ?? 'no_pace_evidence']}
        </p>
        {projection.withheldReason === 'no_daily_goal' && (
          <Button variant="secondary" onClick={() => onNavigate('/me/settings')}>
            Set a daily goal
          </Button>
        )}
      </section>
    );
  }

  return (
    <section>
      <h2>What comes next</h2>
      {/* The working, shown. A date whose basis is hidden is the competitor's graph. */}
      <p className="vl-page__lede">
        At {projection.assumedMinutesPerDay} minutes a day, and the {projection.minutesPerSublevel}{' '}
        minutes a sub-level has cost you so far.
      </p>
      <ol className="vl-level-path">
        {projection.milestones.map((milestone) => (
          <li key={milestone.level}>
            <strong>
              {milestone.level} · {milestone.label}
            </strong>{' '}
            — about {Math.round(milestone.etaDays)} days, give or take{' '}
            {Math.round(milestone.uncertaintyDays)}
          </li>
        ))}
      </ol>
      {projection.truncated && (
        <p className="vl-settings__explainer">
          We stop at four sub-levels. Further out the arithmetic still works and the claim does not.
        </p>
      )}
    </section>
  );
}

/** Why there is no path yet, in the learner's terms rather than the reason code's. */
const WITHHELD_COPY: Record<string, string> = {
  no_pace_evidence:
    'Once you have two level estimates we can show you what comes next, measured from your own practice rather than an average.',
  insufficient_practice:
    'A few more spoken minutes and we can measure your pace properly. We would rather wait than guess at it.',
  window_too_short:
    'Your estimates are too close together to read a pace from yet. Give it a week.',
  no_measured_movement:
    'Your level has held steady, so there is no rate to project from. That is common, and it is not a plateau — it usually means the next sub-level is mid-climb.',
  already_at_target: 'You are at the top of what we measure. The practice still counts.',
  no_daily_goal: 'Set a daily goal and we can turn your measured pace into dates.',
};

// ── F3 Report cards ─────────────────────────────────────────────────────────

export function ReportsScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { requestPaywall, track } = useApp();

  const query = useQuery({
    queryKey: ['reports', courseId] as const,
    queryFn: () => api.call('listReports', { query: { courseId, limit: 30 } }),
  });

  // Whether the list was cut by the tier is recorded with the view, so "how often does
  // the free limit actually bite" is answerable without inferring it from upgrades.
  const reports = query.data;
  useEffect(() => {
    if (reports === undefined) return;
    track('reports_view', {
      count: reports.items.length,
      truncated_by_tier: reports.truncatedByTier,
    });
    if (reports.truncatedByTier) track('reports_truncation_prompt_view', {});
  }, [reports, track]);

  return (
    <main id="main" className="vl-page">
      <h1>Report cards</h1>

      {query.isPending && <Skeleton lines={5} label="Loading your reports" />}
      {query.error !== null && (
        <ErrorState
          title="We couldn't load your reports"
          {...retryIf(query.error, () => void query.refetch())}
        />
      )}

      {query.data !== undefined && (
        <>
          {query.data.items.length === 0 ? (
            <EmptyState
              title="No reports yet"
              body="You get one after every session."
              action={{ label: 'Start a lesson', onClick: () => onNavigate('/learn/path') }}
            />
          ) : (
            <ul className="vl-report-list">
              {query.data.items.map((report) => (
                <li key={report.sessionId}>
                  <button
                    type="button"
                    className="vl-report-row"
                    onClick={() => {
                      // How old a report is when opened says whether history is worth
                      // keeping — which is exactly what the tier limit is selling.
                      track('report_open', {
                        session_id: report.sessionId,
                        age_days: Math.max(
                          0,
                          Math.floor((Date.now() - report.createdAt) / 86_400_000),
                        ),
                      });
                      onNavigate(`/me/reports/${report.sessionId}`);
                    }}
                  >
                    <span>{new Date(report.createdAt).toLocaleDateString()}</span>
                    <span className="vl-report-row__kind">{report.kind}</span>
                    <span className="vl-report-row__score">
                      {report.overall === null || report.band === null
                        ? 'Not scored'
                        : `${Math.round(report.overall)} · ${report.band.label}`}
                    </span>
                    <span className="vl-report-row__goal" data-goal={report.goalAchieved}>
                      {report.goalAchieved === 'yes'
                        ? 'Goal met'
                        : report.goalAchieved === 'partial'
                          ? 'Partly'
                          : 'Not this time'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* The free tier keeps seven days of history. Saying what is behind the line
              is honest; silently truncating is what makes people feel tricked. */}
          {query.data.truncatedByTier && (
            <Banner
              tone="info"
              action={{ label: 'See plans', onClick: () => requestPaywall('deep_analysis') }}
            >
              You're seeing your recent reports. Plus keeps the full history, and nothing already
              shown is taken away.
            </Banner>
          )}
        </>
      )}
    </main>
  );
}

// ── F4 Achievements ─────────────────────────────────────────────────────────

/**
 * Keys already reported this session. Module-level rather than a ref because the screen
 * can be unmounted and remounted by navigation, and an unlock is a once-ever event.
 */
const reported = new Set<string>();

export function AchievementsScreen(): JSX.Element {
  const api = useApi();
  const { track } = useApp();
  const query = useQuery({
    queryKey: ['achievements'] as const,
    queryFn: () => api.call('listAchievements', {}),
  });

  const achievements = query.data?.achievements;
  useEffect(() => {
    if (achievements === undefined) return;

    // Counted per tier, which is what is actually unlockable: an achievement with
    // three tiers is three things to earn, and counting it as one would understate
    // the whole system.
    const tiers = achievements.flatMap((achievement) =>
      achievement.tiers.map((tier) => ({ key: achievement.key, ...tier })),
    );
    const unlocked = tiers.filter((tier) => tier.unlockedAt !== null);
    track('achievements_view', { unlocked: unlocked.length, total: tiers.length });

    // Once per tier, keyed on the server's own `unlockedAt`, never on a re-render: the
    // spec is explicit about that, because a celebration event that repeats would make
    // the retention numbers nonsense.
    for (const tier of unlocked) {
      const id = `${tier.key}:${tier.tier}`;
      if (reported.has(id)) continue;
      reported.add(id);
      track('achievement_unlocked', { key: tier.key, tier: tier.tier });
    }
  }, [achievements, track]);

  return (
    <main id="main" className="vl-page">
      <h1>Achievements</h1>
      {/* Effort, not spend. Nothing here is purchasable, and nothing expires. */}
      <p className="vl-page__lede">
        These track what you have actually done. None of them can be bought, and none of them
        disappear if you take a week off.
      </p>

      {query.isPending && <Skeleton lines={5} label="Loading achievements" />}
      {query.error !== null && (
        <ErrorState
          title="We couldn't load achievements"
          {...retryIf(query.error, () => void query.refetch())}
        />
      )}

      {query.data !== undefined && (
        <ul className="vl-achievements">
          {query.data.achievements.map((achievement) => {
            const earned = achievement.tiers.filter((tier) => tier.unlockedAt !== null).length;
            const next = achievement.tiers.find((tier) => tier.unlockedAt === null);
            return (
              <li key={achievement.key} className="vl-achievement">
                <span className="vl-achievement__name">{achievement.name}</span>
                <span className="vl-achievement__progress">
                  {earned} of {achievement.tiers.length}
                </span>
                {next !== undefined && (
                  <span className="vl-achievement__next">Next at {next.target}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}

// ── F5 Settings ─────────────────────────────────────────────────────────────

/**
 * The store's own subscription page.
 *
 * The only place a store subscription can actually be cancelled: our API has no say
 * in it and deliberately exposes no manage URL, because payment data never touches
 * our systems.
 */
/**
 * Opens the one place this subscription can actually be cancelled.
 *
 * This used to be a hard-coded link to Apple for everybody, so a learner who subscribed
 * on Android and opened the web app landed on a page where their subscription does not
 * appear — which is indistinguishable, from the outside, from the retention dark
 * pattern the section below exists to avoid. `research/12` fourth pass has the
 * competitor reviews this produces.
 */
function openCancellation(route: CancellationRoute | null): void {
  // Every route with a URL is a store's own page, which a core test enforces. A store
  // with no self-serve page has `url: null` and no button, so there is nothing here to
  // handle for it.
  if (route?.url == null) return;
  globalThis.open?.(route.url, '_blank', 'noreferrer');
}

/**
 * Offline, which the paywall sells and which nothing implemented.
 *
 * Its own component because it owns a store and three mutations, and settings was
 * already the largest screen in the app before this section existed.
 */
function OfflineSettings(): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();

  const entitlements = useQuery({
    queryKey: qk.entitlements,
    queryFn: () => api.call('getEntitlements'),
  });

  /** The same store the API client reads through, so the totals agree. */
  const [offlineStore] = useState(() => new IndexedDbStore());

  /** How much is held on this device, for the storage total the spec asks for. */
  const offlineHeld = useQuery({
    queryKey: ['offline', 'held'],
    queryFn: () => offlineStore.describe(),
  });

  /**
   * Walks the manifest and fetches what is missing.
   *
   * Each lesson goes through the normal typed call, so it is validated against the
   * contract and written to the same store the offline read path uses — downloading is
   * nothing more than visiting every lesson once, ahead of time.
   */
  const download = useMutation({
    mutationFn: async () => {
      const manifest = await api.call('getContentManifest', { query: { courseId, since: 0 } });
      const held = await offlineStore.describe();
      const plan = planOfflineSync(manifest, {
        version: manifest.version,
        ids: [],
        bytes: held.bytes,
      });

      let saved = 0;
      let failed = 0;
      for (const entry of plan.fetch) {
        if (entry.kind !== 'lesson') continue;
        try {
          await api.call('getLesson', { params: { lessonId: entry.id } });
          saved += 1;
        } catch {
          // One lesson failing is not the download failing; the count says how many.
          failed += 1;
        }
      }
      return { saved, failed };
    },
    onSuccess: () => void offlineHeld.refetch(),
  });

  const clearOffline = useMutation({
    mutationFn: () => offlineStore.clear(),
    onSuccess: () => void offlineHeld.refetch(),
  });

  return (
    <>
      {/* Offline, which the paywall sells as `Offline lessons` on all three clients
            and which nothing implemented: `GET /content/manifest` was in the contract,
            handled and indexed, with no client calling it. Selling a feature that does
            not exist is the review class `tooling/scripts/verify-promises.mjs` exists to
            prevent, and this was one. */}
      <section className="vl-settings__section">
        <h2>Offline</h2>
        {entitlements.data?.entitlement.limits.offlineDownloads === true ? (
          <>
            <p className="vl-settings__explainer">
              {offlineHeld.data === undefined
                ? 'Checking what you have…'
                : `${formatBytes(offlineHeld.data.bytes)} saved on this device${
                    offlineHeld.data.count === 0
                      ? '. Lessons you open are kept automatically.'
                      : ` across ${offlineHeld.data.count} item${offlineHeld.data.count === 1 ? '' : 's'}.`
                  }`}
            </p>
            <Button
              variant="secondary"
              loading={download.isPending}
              onClick={() => download.mutate()}
            >
              Download this course
            </Button>
            {download.data !== undefined && (
              <p className="vl-settings__explainer" role="status">
                {download.data.failed === 0
                  ? `Saved ${download.data.saved} lesson${download.data.saved === 1 ? '' : 's'}. They work with no signal.`
                  : `Saved ${download.data.saved}. ${download.data.failed} didn't come down — try again on a better connection.`}
              </p>
            )}
            <Button
              variant="ghost"
              loading={clearOffline.isPending}
              onClick={() => clearOffline.mutate()}
            >
              Remove downloads
            </Button>
          </>
        ) : (
          <p className="vl-settings__explainer">
            Downloading lessons to practise with no signal is part of Plus. Lessons you have already
            opened still work offline.
          </p>
        )}
      </section>
    </>
  );
}

/**
 * Referrals, which `docs/09-growth.md` §5 specified and nothing implemented.
 *
 * Verbi ships a referral code with no stated incentive; this one states what the
 * referred learner has to do, because a condition nobody can read is a programme that
 * looks broken.
 */
function ReferralSettings(): JSX.Element {
  const api = useApi();
  const [entered, setEntered] = useState('');

  const referral = useQuery({
    queryKey: ['referral'],
    queryFn: () => api.call('getReferral'),
  });

  const redeem = useMutation({
    mutationFn: (code: string) => api.call('redeemReferral', { body: { code } }),
    onSuccess: () => void referral.refetch(),
  });

  return (
    <section className="vl-settings__section">
      <h2>Invite someone</h2>
      {referral.data === undefined ? (
        <Skeleton lines={2} label="Loading your code" />
      ) : (
        <>
          <p className="vl-settings__code">{referral.data.code}</p>
          <p className="vl-settings__explainer">{referral.data.requirement}</p>
          {referral.data.redeemed > 0 && (
            <p className="vl-settings__explainer">
              {referral.data.redeemed} used it · {referral.data.qualified} finished three lessons ·{' '}
              {referral.data.monthsEarned} month{referral.data.monthsEarned === 1 ? '' : 's'} earned
            </p>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard?.writeText(referral.data?.code ?? '');
            }}
          >
            Copy my code
          </Button>
        </>
      )}

      <label className="vl-field">
        <span className="vl-field__label">Got a code from someone?</span>
        <input
          className="vl-field__input"
          value={entered}
          onChange={(event) => setEntered(event.target.value.toUpperCase())}
          placeholder="ABCD2345"
          maxLength={16}
          autoComplete="off"
        />
      </label>
      <Button
        variant="secondary"
        disabled={entered.length < 4}
        loading={redeem.isPending}
        onClick={() => redeem.mutate(entered)}
      >
        Use it
      </Button>
      {redeem.data !== undefined && (
        <Banner tone={redeem.data.accepted ? 'success' : 'warning'}>{redeem.data.message}</Banner>
      )}
    </section>
  );
}

/**
 * Which account this is, and the way out of it.
 */
function AccountSettings(): JSX.Element {
  const api = useApi();
  const me = useQuery({ queryKey: qk.me, queryFn: () => api.call('getMe') });

  /**
   * Signing out tells the server, then forgets locally.
   *
   * `POST /auth/logout` revokes the refresh-token family. Dropping the tokens from this
   * browser alone would leave that family alive, which is the difference between signing
   * out of a shared computer and merely closing the tab.
   */
  /**
   * Registers a passkey on this device.
   *
   * The server issues the challenge, verifies the ceremony and stores the public key
   * (`apps/api/src/lib/webauthn.ts`). Everything here is the browser's own API, so this
   * needed no dependency — what it needed was the three routes the contract was
   * missing, which is why §F8 recorded passkeys as not built.
   */
  const addPasskey = useMutation({
    mutationFn: async () => {
      const challenge = await api.call('authPasskeyChallenge', { body: { intent: 'register' } });
      const created = await createPasskey(challenge, {
        id: me.data?.id ?? 'account',
        name: me.data?.email ?? 'Say Sooner learner',
      });
      // Null is a dismissal: they closed the system sheet, which is an answer.
      if (created === null) return null;
      return api.call('authPasskeyRegister', { body: { ...created, label: 'This device' } });
    },
    onSuccess: () => void me.refetch(),
  });

  /**
   * Links a Google account, when this build has a client id.
   *
   * The ID token Google signed crosses the wire, never a subject we read out of it —
   * a client-supplied subject is exactly what the server refuses to trust.
   */
  const signInWithGoogle = useMutation({
    mutationFn: async () => {
      const token = await googleIdToken();
      // Null is a dismissal or a blocked script, both of which are answers.
      if (token === null) return null;
      await api.call('authLink', { body: { provider: 'google', credential: token } });
      return true;
    },
    onSuccess: () => void me.refetch(),
  });

  const signOut = useMutation({
    mutationFn: async () => {
      await api.signOut();
      // Back to the start, because every query in memory belongs to the account that
      // just ended.
      globalThis.location.assign('/');
    },
  });

  return (
    <>
      <ReferralSettings />

      {/* Which account this is, in plain terms. Every learner here is anonymous unless
            they linked an identity, and nothing in any client ever said so or offered a
            way to change it — `POST /auth/link` had never been called by anything. An
            anonymous account is the product working as designed (nothing is asked for
            before the first lesson), but a learner who does not know they have one loses
            everything the day they change device. */}
      <section className="vl-settings__section">
        <h2>Your account</h2>
        {me.data?.kind === 'anonymous' ? (
          <>
            <p className="vl-settings__explainer">
              You are signed in on this browser only. Nothing is linked to you, which is why we
              never asked you for anything — and why this progress lives here and nowhere else.
              Clear your browser data and it is gone.
            </p>
            {googleSignInAvailable() && (
              <>
                <Button
                  variant="secondary"
                  loading={signInWithGoogle.isPending}
                  onClick={() => signInWithGoogle.mutate()}
                >
                  Continue with Google
                </Button>
                {signInWithGoogle.error !== null && (
                  <Banner tone="warning">
                    That sign-in didn&apos;t go through. Your progress is safe on this device.
                  </Banner>
                )}
              </>
            )}

            {/* A passkey is the one way to carry an anonymous account to another
                device without asking anybody for an email address, which is why it is
                offered here rather than behind a provider sign-in. */}
            {passkeysSupported() ? (
              <>
                <Button
                  variant="secondary"
                  loading={addPasskey.isPending}
                  onClick={() => addPasskey.mutate()}
                >
                  Add a passkey
                </Button>
                <p className="vl-settings__explainer">
                  A passkey lets you sign in on another device with your face, fingerprint or screen
                  lock — no password and no email address. Your device keeps the secret; we only
                  ever see the public half.
                </p>
                {addPasskey.data != null && (
                  <Banner tone="success">
                    Passkey added. You can sign in with it on any device you are signed into.
                  </Banner>
                )}
                {addPasskey.error !== null && (
                  <Banner tone="warning">That passkey wasn&apos;t added. Nothing changed.</Banner>
                )}
              </>
            ) : (
              <Banner tone="info">
                This browser doesn&apos;t support passkeys. Download my data keeps you a copy in the
                meantime.
              </Banner>
            )}
          </>
        ) : (
          <>
            <p className="vl-settings__explainer">
              Signed in{me.data?.email == null ? '' : ` as ${me.data.email}`}. Your progress follows
              you to any device you sign in on.
            </p>
            <Button
              variant="secondary"
              loading={signOut.isPending}
              onClick={() => signOut.mutate()}
            >
              Sign out
            </Button>
          </>
        )}
      </section>
    </>
  );
}

/**
 * The course settings, which no client could change before.
 */
function CourseSettings(): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();

  // From the list, because there is no single-course route: the table has
  // `listCourses` and `PATCH /courses/:id`, and one course is a find away.
  const courses = useQuery({ queryKey: qk.courses, queryFn: () => api.call('listCourses') });
  const course = { data: courses.data?.courses.find((entry) => entry.id === courseId) };

  const patchCourse = useMutation({
    mutationFn: (body: Omit<RouteBody<'patchCourse'>, 'rowV'>) =>
      api.call('patchCourse', {
        params: { courseId },
        // The row version the server gave us: a stale write is rejected rather than
        // merged, so two devices cannot quietly overwrite each other's settings.
        body: { ...body, rowV: course.data?.rowV ?? 1 },
      }),
    onSuccess: () => void courses.refetch(),
  });

  /** Saves one course setting and records it as one `settings_changed`. */
  const changeCourse = (
    key: string,
    value: AnalyticsValue,
    body: Omit<RouteBody<'patchCourse'>, 'rowV'>,
  ) => {
    track('settings_changed', { key, value });
    patchCourse.mutate(body);
  };

  return (
    <>
      {course.data !== undefined && (
        <section className="vl-settings__section">
          <h2>Your course</h2>

          <fieldset className="vl-settings__fieldset">
            <legend>Dialect</legend>
            {DIALECTS_BY_LANGUAGE[course.data.language].map((dialect) => (
              <label key={dialect} className="vl-radio">
                <input
                  type="radio"
                  name="dialect"
                  checked={course.data?.dialect === dialect}
                  onChange={() => changeCourse('dialect', dialect, { dialect })}
                />
                {dialect}
              </label>
            ))}
            <p className="vl-settings__explainer">
              Changes the voice you hear and the words you are taught. Your progress carries over.
            </p>
          </fieldset>

          <fieldset className="vl-settings__fieldset">
            <legend>Minutes a day</legend>
            {DAILY_GOAL_CHOICES.map((minutes) => (
              <label key={minutes} className="vl-radio">
                <input
                  type="radio"
                  name="dailyGoalMinutes"
                  checked={course.data?.dailyGoalMinutes === minutes}
                  onChange={() =>
                    changeCourse('dailyGoalMinutes', minutes, { dailyGoalMinutes: minutes })
                  }
                />
                {minutes} min
              </label>
            ))}
            {/* The same sentence onboarding showed, from the same function: a
                  projection that changed wording between the two screens would read as
                  two different claims. */}
            <p className="vl-settings__explainer">
              {commitmentProjection(course.data.dailyGoalMinutes)}
            </p>
          </fieldset>

          <fieldset className="vl-settings__fieldset">
            <legend>How fast the tutor speaks</legend>
            {(
              [
                [0.8, 'Slower than normal'],
                [1, 'Normal speed'],
                [1.2, 'Faster, to stretch me'],
              ] as const
            ).map(([rate, label]) => (
              <label key={rate} className="vl-radio">
                <input
                  type="radio"
                  name="speechRate"
                  checked={course.data?.persona.speechRate === rate}
                  onChange={() =>
                    changeCourse('persona.speechRate', rate, { persona: { speechRate: rate } })
                  }
                />
                {label}
              </label>
            ))}
          </fieldset>

          <fieldset className="vl-settings__fieldset">
            <legend>When to correct me</legend>
            {(
              [
                ['inline', 'As it happens'],
                ['after_turn', 'After each turn'],
                ['end_of_session', 'At the end'],
              ] as const
            ).map(([style, label]) => (
              <label key={style} className="vl-radio">
                <input
                  type="radio"
                  name="correctionStyle"
                  checked={course.data?.persona.correctionStyle === style}
                  onChange={() =>
                    changeCourse('persona.correctionStyle', style, {
                      persona: { correctionStyle: style },
                    })
                  }
                />
                {label}
              </label>
            ))}
            <p className="vl-settings__explainer">
              Interrupting helps accuracy and hurts fluency. Correcting at the end does the
              opposite. Neither is wrong.
            </p>
          </fieldset>

          <fieldset className="vl-settings__fieldset">
            <legend>Your language in lessons</legend>
            {(
              [
                ['l1_supported', 'Show English when it helps'],
                ['target_only', 'Target language only'],
              ] as const
            ).map(([value, label]) => (
              <label key={value} className="vl-radio">
                <input
                  type="radio"
                  name="scaffolding"
                  checked={course.data?.scaffolding === value}
                  onChange={() => changeCourse('scaffolding', value, { scaffolding: value })}
                />
                {label}
              </label>
            ))}
            <p className="vl-settings__explainer">
              Target-only means we do not translate unprompted. Asking still works — asking is
              prompting.
            </p>
          </fieldset>

          {patchCourse.error !== null && (
            <p className="vl-settings__explainer" role="status">
              That didn&apos;t save. The setting is unchanged.
            </p>
          )}
        </section>
      )}
    </>
  );
}

export function SettingsScreen(_: NavigationProps): JSX.Element {
  const api = useApi();
  const { isSubscribed, requestPaywall, setAnalyticsOptOut, track } = useApp();
  /** Deletion asks twice, in place, and says exactly what goes. */
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const entitlements = useQuery({
    queryKey: qk.entitlements,
    queryFn: () => api.call('getEntitlements', {}),
  });

  const me = useQuery({ queryKey: qk.me, queryFn: () => api.call('getMe', {}) });

  // Derived from the store that actually holds the subscription, not from the platform
  // the learner happens to be using. Those differ exactly when it matters most.
  const cancellation = cancellationRoute(entitlements.data?.entitlement.store ?? null);

  // The tracker starts out collecting, because the preference is not known until the
  // profile loads. Applying it here means a learner who opted out on another device
  // stops being measured on this one as soon as the app knows — and the queue they
  // accumulated before that is dropped rather than sent.
  const optedOut = me.data?.privacy.analyticsOptOut;
  useEffect(() => {
    if (optedOut !== undefined) setAnalyticsOptOut(optedOut);
  }, [optedOut, setAnalyticsOptOut]);

  const patch = useMutation({
    mutationFn: (body: RouteBody<'patchMe'>) => api.call('patchMe', { body }),
    onSuccess: () => void me.refetch(),
  });

  /**
   * Saves a preference and records it as one `settings_changed`.
   *
   * One path for every toggle, so a new setting cannot be added without being
   * measured, and the key is the contract's own field name rather than the label on
   * screen — labels get reworded and then the history stops joining up.
   */
  const change = (key: string, value: AnalyticsValue, body: RouteBody<'patchMe'>) => {
    track('settings_changed', { key, value });
    patch.mutate(body);
  };

  const notifications = useQuery({
    queryKey: qk.notifications,
    queryFn: () => api.call('getNotificationPrefs', {}),
  });

  const setNotification = useMutation({
    mutationFn: (body: RouteBody<'setNotificationPref'>) =>
      api.call('setNotificationPref', { body }),
    onSuccess: () => void notifications.refetch(),
  });

  /**
   * Files the refund request, which is the half of this flow that is ours.
   *
   * The store issues the money; we record why, and the server checks whether this
   * learner actually hit degraded sessions — in which case it grants a month and says
   * so. That check cannot happen if the client just opens the store.
   */
  const refund = useMutation({
    mutationFn: (reason: RouteBody<'requestRefund'>['reason']) =>
      api.call('requestRefund', { body: { reason } }),
  });

  const deleteAccount = useMutation({
    mutationFn: () => api.call('deleteMe', { body: {} }),
  });

  const deleteRecordings = useMutation({
    // Repeats while the server says more remain: a learner with a year of practice has
    // more objects than one call removes, and "deleted" has to mean all of them.
    mutationFn: async () => {
      let deleted = 0;
      let more = true;
      while (more) {
        const result = await api.call('deleteRecordings', { body: {} });
        deleted += result.deleted;
        more = result.more;
      }
      return deleted;
    },
  });

  const [exportRequestId, setExportRequestId] = useState<string | null>(null);

  /**
   * Polls until the file is built, or until it is clear that it will not be.
   *
   * The export is prepared on a queue, so the request returns before the file exists and
   * the status says `pending` rather than 404 until it does. But `pending` is also what
   * an export that never got built looks like — the status is derived from whether the
   * object is in the bucket, and nothing marks a request as abandoned. Polling that
   * forever told the learner "we're putting it together" indefinitely for a file that
   * was not coming, under the one commitment that has a legal deadline attached.
   *
   * The queue runs within seconds, so two minutes is not a slow export; it is a lost
   * one. `failed` stops immediately — the enum has always carried it.
   */
  const exportStatus = useQuery({
    queryKey: ['export', exportRequestId],
    enabled: exportRequestId !== null,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data === undefined) return 2000;
      if (data.status !== 'pending') return false;
      return isExportStalled(data) ? false : 2000;
    },
    queryFn: () => api.call('getExport', { params: { requestId: exportRequestId ?? '' } }),
  });

  const exportData = useMutation({
    // Audio included by default: a data export that silently drops the recordings is
    // not the thing the learner asked for.
    mutationFn: () => api.call('exportMe', { body: { format: 'json+audio' } }),
    onSuccess: (result) => setExportRequestId(result.requestId),
  });

  // One value, three banners. The conditions used to be `status !== 'ready'` and nothing
  // else, so the lost case had no condition of its own and read as "putting it together".
  const phase = exportPhase(exportData.data !== undefined, exportStatus.data);

  return (
    <main id="main" className="vl-page">
      <h1>Settings</h1>

      {/* Plan first, cancellation one tap in. Burying the exit is what turns a lapsed
          subscriber into a one-star review. */}
      <section className="vl-settings__section">
        <h2>Your plan</h2>
        {entitlements.data !== undefined && (
          <>
            <p className="vl-settings__plan">
              {entitlements.data.entitlement.tier === 'free'
                ? 'Free'
                : entitlements.data.entitlement.tier.replace(/^./, (c) => c.toUpperCase())}
              {entitlements.data.entitlement.inTrial && ' · trial'}
              {entitlements.data.entitlement.periodEnd !== null &&
                ` · ${entitlements.data.entitlement.inTrial ? 'ends' : 'renews'} ${new Date(
                  entitlements.data.entitlement.periodEnd,
                ).toLocaleDateString()}`}
            </p>

            <UsageMeter
              voiceSecondsUsed={entitlements.data.usage.voiceSecondsUsed}
              voiceSecondsTotal={entitlements.data.usage.voiceSecondsTotal}
              state={entitlements.data.usage.state}
              resetsAt={entitlements.data.usage.resetsAt}
            />

            {/* The store's own subscription page, which is the only place a store
                subscription can be cancelled. This navigated to an in-app route that
                does not exist, so the button did nothing — the accidental version of
                the dark pattern the section below is about. */}
            {isSubscribed && cancellation !== null ? (
              <>
                {/* Named, not implied. "Manage your subscription" with no store in it
                    is what leaves a learner hunting through the wrong account. */}
                <p className="vl-settings__explainer">{cancellation.explanation}</p>
                {cancellation.url !== null && (
                  <Button variant="secondary" onClick={() => openCancellation(cancellation)}>
                    {cancellation.label}
                  </Button>
                )}
              </>
            ) : isSubscribed ? null : (
              <Button onClick={() => requestPaywall('post_first_lesson')}>See plans</Button>
            )}
          </>
        )}
      </section>

      {/* Cancellation one tap in, and the refund route next to it. Burying either is
          what turns a lapsed subscriber into a one-star review — "subscription/refund"
          is a third of the competitor's negative reviews. */}
      {isSubscribed && (
        <section className="vl-settings__section">
          <h2>Cancelling</h2>
          {/* Rendered only when there is somewhere to go. A store with no self-serve
              page — a web subscription, a promotional month — gets the explanation and
              the request list below instead, because a button that goes nowhere is the
              bug this whole section is a fix for. */}
          {cancellation?.url !== null && cancellation !== null && (
            <Button
              variant="secondary"
              onClick={() => {
                // Entering the flow is recorded separately from completing it: the gap
                // between the two is the only measure of whether the win-back offer is
                // persuasion or obstruction.
                track('cancel_flow_entered', {});
                openCancellation(cancellation);
              }}
            >
              {cancellation.label}
            </Button>
          )}
          {cancellation !== null && (
            <p className="vl-settings__explainer">{cancellation.explanation}</p>
          )}
          {/* This used to be a button that opened the store and recorded an event.
              `POST /billing/refund-request` existed the whole time and nothing called
              it, so the server's goodwill path never ran once: a learner with three or
              more degraded sessions in the last month is owed a free month, and the
              only way to find that out is to ask. Opening the store silently skipped
              the one part of this flow that was on our side. */}
          {refund.data === undefined ? (
            <>
              <p className="vl-settings__explainer">Why are you asking for a refund?</p>
              {(
                [
                  ['technical_problem', 'It kept going wrong'],
                  ['not_as_expected', "It isn't what I expected"],
                  ['accidental', "I didn't mean to subscribe"],
                  ['too_expensive', "It's too expensive"],
                  ['other', 'Another reason'],
                ] as const
              ).map(([reason, label]) => (
                <Button
                  key={reason}
                  variant="ghost"
                  loading={refund.isPending}
                  onClick={() => {
                    // The event declares no fields; the reason belongs in the
                    // feedback row the server writes, which is the record a human
                    // actually reads when deciding a refund.
                    track('refund_requested', {});
                    refund.mutate(reason);
                  }}
                >
                  {label}
                </Button>
              ))}
            </>
          ) : (
            <Banner tone={refund.data.goodwillGranted ? 'success' : 'info'}>
              {refund.data.message}{' '}
              <a href={refund.data.storeRefundUrl} target="_blank" rel="noreferrer">
                Open the store's refund page
              </a>
            </Banner>
          )}
          {refund.error !== null && (
            <Banner tone="warning">
              We couldn&apos;t file that. The store can still refund you directly.
            </Banner>
          )}
          <p className="vl-settings__explainer">
            Cancelling takes effect at the end of the period you have paid for, and nothing you have
            done is deleted. Refunds go through the store that took the payment; we will tell you
            exactly how.
          </p>
        </section>
      )}

      {/* The course is the learner's, and until now none of it could be changed after
          onboarding: `PATCH /courses/:id` has been in the contract from the start and
          no client had ever called it. Dialect is the one this product sells, and the
          daily goal drives the ring on Today, the streak and the projection — being
          stuck with the answer you gave on day one is the opposite of what a course
          that adapts means. */}
      {/* Three sections, each its own component: they query what they need through
          the shared cache, so none of them is a prop drilled down from here. */}
      <OfflineSettings />
      <AccountSettings />
      <CourseSettings />

      {/* The contract published, not just honoured. §8 promises the learner a cap, a
          quiet window and a switch per type; a promise they cannot read is not one they
          can hold us to, so the limits come from the server with the toggles. */}
      <section className="vl-settings__section">
        <h2>Notifications</h2>
        {notifications.error !== null && (
          <>
            <p className="vl-settings__explainer">
              We couldn&apos;t reach your notification settings. Nothing has changed.
            </p>
            <Button variant="secondary" onClick={() => void notifications.refetch()}>
              Try again
            </Button>
          </>
        )}
        {notifications.data !== undefined && (
          <>
            <p className="vl-settings__explainer">
              Never more than {notifications.data.limits.maxPerDay} a day, never between{' '}
              {notifications.data.limits.quietFromHour}:00 and{' '}
              {notifications.data.limits.quietUntilHour}:00 your time, and every one opens the thing
              it is about rather than the home screen.
            </p>

            {notifications.data.pushRegisteredAt === null && (
              <p className="vl-settings__explainer">
                This browser has not been given permission to show them, so these switches decide
                what we would send rather than what you will see.
              </p>
            )}

            {notifications.data.types.map((entry) => (
              <label className="vl-toggle" key={entry.type}>
                <input
                  type="checkbox"
                  checked={!entry.muted}
                  onChange={(event) => {
                    track('settings_changed', {
                      key: `notifications.${entry.type}`,
                      value: event.target.checked,
                    });
                    setNotification.mutate({ type: entry.type, muted: !event.target.checked });
                  }}
                />
                {entry.title}
                <span className="vl-settings__explainer">{entry.detail}</span>
              </label>
            ))}
          </>
        )}
      </section>

      <section className="vl-settings__section">
        <h2>Usage data</h2>
        <label className="vl-toggle">
          <input
            type="checkbox"
            checked={me.data?.privacy.analyticsOptOut ?? false}
            onChange={(event) => {
              // Applied to the tracker immediately as well as saved, so the queue is
              // dropped on the tap rather than on the next profile load.
              setAnalyticsOptOut(event.target.checked);
              // Recorded before it is applied: once the tracker is off, the event
              // saying it was turned off would never be sent.
              change('privacy.analyticsOptOut', event.target.checked, {
                privacy: { analyticsOptOut: event.target.checked },
              });
            }}
          />
          Don&apos;t count how I use the app
        </label>
        <p className="vl-settings__explainer">
          We record which screens you reach and how practice goes — never what you said. Turning
          this off stops it on every device, and nothing already queued on this one is sent.
        </p>
      </section>

      <section className="vl-settings__section">
        <h2>Your recordings</h2>
        {/* Off by default. A consent that defaults to on is not a consent. */}
        <label className="vl-toggle">
          <input
            type="checkbox"
            checked={me.data?.privacy.trainingOptIn ?? false}
            onChange={(event) =>
              change('privacy.trainingOptIn', event.target.checked, {
                privacy: { trainingOptIn: event.target.checked },
              })
            }
          />
          Help improve scoring
        </label>
        <p className="vl-settings__explainer">
          Off by default. When on, some of your recordings may be reviewed by our team to improve
          pronunciation scoring. Turning it off deletes anything kept for that purpose.
        </p>

        <fieldset className="vl-settings__fieldset">
          <legend>Keep my recordings</legend>
          {(
            [
              ['delete_after_scoring', 'Delete as soon as they are scored'],
              ['keep_30d', 'Keep for 30 days so I can listen back'],
              ['keep', 'Keep until I delete them'],
            ] as const
          ).map(([value, label]) => (
            <label key={value} className="vl-radio">
              <input
                type="radio"
                name="recordingPolicy"
                checked={me.data?.privacy.recordingPolicy === value}
                onChange={() =>
                  change('privacy.recordingPolicy', value, {
                    privacy: { recordingPolicy: value },
                  })
                }
              />
              {label}
            </label>
          ))}
        </fieldset>

        {/* The bulk delete `docs/10-quality-privacy.md` §5 promises alongside the
            switch. The retention policy decides what happens to recordings from now on;
            this is the learner deciding about the ones they already have. */}
        <Button
          variant="secondary"
          loading={deleteRecordings.isPending}
          onClick={() => deleteRecordings.mutate()}
        >
          Delete my recordings
        </Button>
        {deleteRecordings.data !== undefined && (
          <p className="vl-settings__explainer" role="status">
            {deleteRecordings.data === 0
              ? 'There were none to delete.'
              : `Deleted ${deleteRecordings.data}. Your scores and progress stay.`}
          </p>
        )}
        {deleteRecordings.error !== null && (
          <p className="vl-settings__explainer" role="status">
            That didn&apos;t go through. Nothing was deleted.
          </p>
        )}
      </section>

      <section className="vl-settings__section">
        <h2>Your data</h2>
        {/* A real download now. The export used to be emailed — and the queue job
            that would have sent it was a `console.warn`, so nothing was built and no
            email ever went. It is built, stored and collected here, which also means
            an anonymous learner can have one. */}
        <Button
          variant="secondary"
          onClick={() => {
            track('data_export_requested', {});
            exportData.mutate();
          }}
          loading={exportData.isPending || exportStatus.data?.status === 'pending'}
        >
          Download everything
        </Button>
        {phase === 'lost' && (
          <Banner tone="warning">
            {/* Said plainly, because the alternative is a learner waiting on a file that
                is not being built. Nothing of theirs is gone — the export only ever
                reads — and asking again rebuilds it from scratch. */}
            That export didn&apos;t finish. Nothing of yours was changed or deleted — ask again and
            we&apos;ll rebuild it.
          </Banner>
        )}
        {phase === 'building' && (
          <Banner tone="info">
            We&apos;re putting it together. It stays on this screen — nothing is deleted.
          </Banner>
        )}
        {exportStatus.data?.status === 'ready' && exportStatus.data.downloadUrl !== null && (
          <Banner tone="success">
            Ready, {formatBytes(exportStatus.data.bytes ?? 0)}.{' '}
            <a href={exportStatus.data.downloadUrl} download>
              Save the file
            </a>
          </Banner>
        )}
        {/* Destructive actions last, and never one-tap. */}
        <Button
          variant="danger"
          onClick={() => {
            // Recorded at the request, not at the confirmation: how many people reach
            // this and stop is the number that says whether the flow is honest or
            // merely obstructive.
            track('account_delete_requested', {});
            // Deletion is confirmed in place. A route that does not exist meant the
            // button did nothing, which for this of all actions reads as a refusal.
            setConfirmingDelete(true);
          }}
        >
          Delete my account
        </Button>
        {confirmingDelete && deleteAccount.data === undefined && (
          <>
            <Banner tone="warning">
              {/* Says exactly what happens, before the second tap — including the restore
                  window. This used to say "cannot be undone", which is not what the
                  server does: it soft-deletes with 30 days to change your mind, and
                  overstating it scares people out of a decision they can reverse. */}
              This schedules your account, your recordings and your progress for deletion.{' '}
              {me.data?.kind === 'anonymous'
                ? 'There is no email on this account, so there is no restore link — once you confirm, this browser loses access and your progress goes with it.'
                : 'You have 30 days to change your mind, and we will email you a link to restore it.'}{' '}
              Your subscription is separate — cancel it in the store, or you will keep being charged
              for an account that no longer exists.
            </Banner>
            {/* The second tap, which did not exist: the banner appeared and there was
                nothing to press, so deletion was impossible on the web — and both mobile
                clients sent the learner here to do it. */}
            <Button
              variant="danger"
              loading={deleteAccount.isPending}
              onClick={() => deleteAccount.mutate()}
            >
              Delete it
            </Button>
            <Button variant="secondary" onClick={() => setConfirmingDelete(false)}>
              Keep my account
            </Button>
          </>
        )}
        {deleteAccount.data !== undefined && (
          <Banner tone="success">
            Scheduled. Restorable until{' '}
            {new Date(deleteAccount.data.hardDeleteAt).toLocaleDateString()}.
          </Banner>
        )}
        {deleteAccount.error !== null && (
          <Banner tone="warning">That didn&apos;t go through. Your account is unchanged.</Banner>
        )}
      </section>

      <section className="vl-settings__section">
        <h2>About</h2>
        <nav className="vl-settings__links">
          <a className="vl-link" href="https://saysooner.com/terms" rel="noreferrer">
            Terms
          </a>
          <a className="vl-link" href="https://saysooner.com/privacy" rel="noreferrer">
            Privacy
          </a>
        </nav>
      </section>
    </main>
  );
}

// ── F6 Leagues ──────────────────────────────────────────────────────────────

/**
 * A weekly cohort ranked on **spoken minutes**, not points.
 *
 * Ranking on XP rewards whoever taps the most, which is the opposite of the behaviour
 * this product exists to produce. Spoken minutes is the thing we want more of, so it
 * is the thing the board is sorted by — and it cannot be farmed by doing easy taps.
 *
 * No public identity beyond a chosen display name, and no league of one: a learner not
 * matched this week is told so rather than shown an empty board with themselves at the
 * top of it.
 */
/**
 * The last league week this device saw, for detecting a promotion.
 *
 * `localStorage`, because this is a per-device convenience rather than state anything
 * depends on: a learner who clears it simply does not get the one-off event, and the
 * board itself is unaffected.
 */
const LEAGUE_KEY = 'saysooner.league';

function readLastLeagueWeek(): { weekStart: string; zone: string } | null {
  try {
    const raw = globalThis.localStorage?.getItem(LEAGUE_KEY);
    return raw === null || raw === undefined
      ? null
      : (JSON.parse(raw) as { weekStart: string; zone: string });
  } catch {
    return null;
  }
}

function writeLastLeagueWeek(value: { weekStart: string; zone: string }): void {
  try {
    globalThis.localStorage?.setItem(LEAGUE_KEY, JSON.stringify(value));
  } catch {
    // Private mode. The board still works; only the one-off event is lost.
  }
}

export function LeaguesScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const { track } = useApp();

  const query = useQuery({
    queryKey: ['league'] as const,
    queryFn: () => api.call('getCurrentLeague', {}),
  });

  const league = query.data;
  const you = league?.members.find((member) => member.isYou) ?? null;

  useEffect(() => {
    if (league === undefined || league === null || you === null) return;
    const zone =
      you.rank <= league.promotionRank
        ? 'promotion'
        : you.rank >= league.demotionRank
          ? 'demotion'
          : 'holding';
    track('league_view', { rank: you.rank, members: league.members.length, zone });

    // Promotion and demotion are week-boundary events, so they are detected by
    // comparing against the last week this device saw rather than by the zone being
    // displayed: standing in the promotion zone on Wednesday is not a promotion.
    const previous = readLastLeagueWeek();
    if (previous !== null && previous.weekStart !== league.weekStart) {
      if (zone === 'promotion' && previous.zone !== 'promotion') {
        track('league_promoted', { from: previous.zone, to: zone });
      } else if (zone === 'demotion' && previous.zone !== 'demotion') {
        track('league_demoted', { from: previous.zone, to: zone });
      }
    }
    writeLastLeagueWeek({ weekStart: league.weekStart, zone });
  }, [league, you, track]);

  if (query.isPending) return <Skeleton lines={6} label="Loading this week's league" />;
  if (query.error !== null) {
    /*
     * Leagues need a registered account, so an anonymous learner gets a 403 carrying
     * `retryable: false` and the sentence "Sign in to use this. Your progress will be
     * kept." This used to render the generic failure with a Try again button: a dead end
     * dressed as a hiccup, with the one thing that would fix it thrown away and a control
     * that could never work. The server already says whether retrying is pointless, so
     * ask it rather than assume.
     */
    const problem = query.error instanceof ApiError ? query.error.problem : null;
    const canRetry = problem?.retryable ?? true;
    return (
      <ErrorState
        title={canRetry ? "We couldn't load your league" : 'Leagues are for signed-in learners'}
        detail={problem?.detail}
        {...(canRetry ? { onRetry: () => void query.refetch() } : {})}
        // Never a dead end. When retrying would help, the alternative is the thing the
        // league measures; when it would not, it is the way to actually get in.
        alternative={
          canRetry
            ? { label: 'Go and speak instead', onClick: () => onNavigate('/talk') }
            : { label: 'Sign in', onClick: () => onNavigate('/me/settings') }
        }
      />
    );
  }

  if (league === null || league === undefined) {
    return (
      <main id="main" className="vl-page">
        <h1>Leagues</h1>
        {/* Said plainly. A league of one is a worse experience than no league. */}
        <EmptyState
          title="You're not in a league this week"
          body="Leagues match 30 learners with similar activity. Speak on a few days this week and you'll be matched on Monday."
          action={{ label: 'Start a conversation', onClick: () => onNavigate('/talk') }}
        />
      </main>
    );
  }

  return (
    <main id="main" className="vl-page vl-league">
      <h1>This week&apos;s league</h1>
      <p className="vl-page__lede">
        Ranked on minutes actually spoken. Nothing here can be earned by tapping.
      </p>

      <ol className="vl-league__board">
        {league.members.map((member) => (
          <li
            key={`${member.rank}-${member.displayName}`}
            data-you={member.isYou}
            data-zone={
              member.rank <= league.promotionRank
                ? 'promotion'
                : member.rank >= league.demotionRank
                  ? 'demotion'
                  : 'holding'
            }
          >
            <span className="vl-league__rank">{member.rank}</span>
            <span className="vl-league__name">{member.displayName}</span>
            <span className="vl-league__minutes">{Math.round(member.spokenSeconds / 60)} min</span>
          </li>
        ))}
      </ol>

      <p className="vl-note">
        Top {league.promotionRank} move up; {league.members.length - league.demotionRank + 1} at the
        bottom move down. You can leave leagues in Settings and nothing else changes.
      </p>
    </main>
  );
}

// ── F4 Monthly assessment ───────────────────────────────────────────────────

/**
 * A formal, recorded, rubric-scored ten minutes, once a month.
 *
 * "Once a month" is a deliberate constraint rather than a limitation. A level estimate
 * that can be re-rolled on demand is a number people retake until they like it, which
 * makes it worthless as a measurement — and the whole reason this exists is that the
 * competitor's level is not believed. When it is too soon, the date is stated.
 */
export function AssessmentScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { requestPaywall, track } = useApp();

  // The entitlement decides whether the assessment is unlocked, not "is paying".
  const entitlements = useQuery({
    queryKey: qk.entitlements,
    queryFn: () => api.call('getEntitlements'),
  });
  const level = useQuery({
    queryKey: ['cefr', courseId] as const,
    queryFn: () => api.call('getCefrDetail', { query: { courseId } }),
  });

  const start = useMutation({
    mutationFn: () =>
      api.call('createSession', {
        body: { id: clientUlid(), courseId, kind: 'assessment', refId: null, mode: 'voice' },
      }),
    onSuccess: (created) => {
      // The level before, recorded at the start, because the point of an assessment is
      // the difference it reports and that is only knowable from both ends. The after
      // and the confidence come from the server once it has scored, so they are nulls
      // here rather than a prediction.
      track('assessment_complete', {
        level_before: level.data?.level ?? null,
        level_after: null,
        confidence: null,
      });
      onNavigate(`/talk/session/${created.session.id}`);
    },
  });

  // `retestAvailable` is the server's decision, not a local date comparison: the
  // monthly window is a rule about the measurement and belongs with it.
  const available = level.data?.retestAvailable ?? false;
  // The limit, not "is paying". This read `!isSubscribed`, so a Plus subscriber was
  // given the monthly assessment — the capability §2 of `08-monetization`, all three
  // plan tables and F4 ("locked (Pro)") reserve for Pro.
  const unlocked = entitlements.data?.entitlement.limits.monthlyAssessment === true;
  const state = level.isPending
    ? 'loading'
    : !unlocked
      ? 'locked'
      : available
        ? 'available'
        : 'too_soon';

  useEffect(() => {
    if (level.isPending) return;
    track('assessment_view', { state });
  }, [state, level.isPending, track]);

  if (level.isPending) return <Skeleton lines={5} label="Checking your assessment" />;

  return (
    <main id="main" className="vl-page vl-assessment">
      <h1>Monthly assessment</h1>
      <p className="vl-page__lede">
        Ten recorded minutes, scored against a rubric rather than against your recent lessons. It
        tightens the confidence on your level and produces a dated summary.
      </p>

      {!unlocked && (
        <Banner
          tone="info"
          action={{ label: 'See what Pro adds', onClick: () => requestPaywall('exam_track') }}
        >
          The formal assessment is part of Pro. Your level still updates from your own speech on
          every plan — this is the version with a rubric and a date on it.
        </Banner>
      )}

      {unlocked && !available && (
        <Banner tone="info">
          {/* The date, not "come back later". */}
          Once a month, so the number means something. Your next one is available next month.
        </Banner>
      )}

      <Button
        disabled={!available || !unlocked || start.isPending}
        loading={start.isPending}
        onClick={() => {
          track('assessment_start', {});
          start.mutate();
        }}
      >
        Start the assessment
      </Button>
    </main>
  );
}

// ── F7 Certificates / level card ────────────────────────────────────────────

/**
 * A dated, shareable level card — and a clear statement when it is not earned yet.
 *
 * The card is generated server-side so the numbers are ours and cannot be edited into
 * a claim we did not make. It is withheld until the level estimate is confident: a
 * certificate is a public artefact, it gets screenshotted, and a number on it the
 * measurement cannot support is the competitor's mistake in its most damaging form.
 */
export function CertificateScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();

  const query = useQuery({
    queryKey: ['certificate', courseId] as const,
    queryFn: () => api.call('getCertificate', { query: { courseId } }),
  });

  const card = query.data;
  useEffect(() => {
    if (card === undefined) return;
    track('certificate_view', { level: card.level, earned: card.earned });
  }, [card, track]);

  if (query.isPending) return <Skeleton lines={6} label="Checking your level card" />;
  if (query.error !== null || card === undefined) {
    return (
      <ErrorState
        title="We couldn't load your level card"
        {...retryIf(query.error, () => void query.refetch())}
      />
    );
  }

  const download = () => {
    if (card.svg === null) return;
    track('certificate_generate', { level: card.level });
    const blob = new Blob([card.svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `saysooner-${card.level ?? 'level'}-${card.issuedOn ?? 'card'}.svg`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main id="main" className="vl-page vl-certificate">
      <h1>Your level card</h1>

      {!card.earned ? (
        <>
          {/* The requirement, stated. Not "keep going". */}
          <EmptyState
            title="Not yet"
            body={card.requires ?? 'A little more scored speaking.'}
            action={{ label: 'Start a conversation', onClick: () => onNavigate('/talk') }}
          />
          <p className="vl-note">
            {card.spokenHours.toFixed(1)} hours spoken so far. The card counts scored speech only —
            lessons opened do not move it, which is what makes it worth having.
          </p>
        </>
      ) : (
        <>
          {/* Rendered inline from the server's own SVG, so what is shared is exactly
              what is shown. */}
          <div
            className="vl-certificate__card"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: the SVG is generated by our Worker from our own data and XML-escapes every interpolated value; rendering it as an <img> would lose the system fonts it relies on.
            dangerouslySetInnerHTML={{ __html: card.svg ?? '' }}
          />

          <div className="vl-step__actions">
            <Button onClick={download}>Download the image</Button>
            {typeof navigator !== 'undefined' && 'share' in navigator && (
              <Button
                variant="secondary"
                onClick={() => {
                  track('certificate_share', { destination: 'system' });
                  void navigator
                    .share?.({
                      title: `My spoken level: ${card.level}`,
                      text: `${card.spokenHours.toFixed(1)} hours of scored speaking.`,
                    })
                    // A declined share sheet is a normal outcome, not an error.
                    .catch(() => undefined);
                }}
              >
                Share
              </Button>
            )}
          </div>
        </>
      )}
    </main>
  );
}
